/**
 * Ensures location permission is granted for outdoor runs.
 *
 * iOS: We proactively request "Always" upfront. This avoids iOS later surprising
 * the user with its automatic background-location reminder dialog mid-run. If the
 * user only grants "When In Use" or denies, we still continue (foreground tracking
 * works; background tracking degrades gracefully).
 *
 * Android: standard location permission is sufficient.
 */
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { registerPlugin } from "@capacitor/core";

export type LocationPermissionReason = "denied" | "whenInUse" | "notGranted";

export type EnsureLocationResult =
  | { ok: true }
  | { ok: false; reason: LocationPermissionReason };

type LocationStatus = "always" | "whenInUse" | "denied" | "notDetermined";

interface StraivLocationPlugin {
  getLocationPermissionStatus?: () => Promise<{ status?: string }>;
  requestAlwaysPermission?: () => Promise<{ status?: string }>;
  openSettings?: () => Promise<void>;
}

interface BGPlugin {
  addWatcher?: (opts: unknown, cb: () => void) => Promise<string>;
  removeWatcher?: (opts: { id: string }) => Promise<void>;
  openSettings?: () => Promise<void>;
}

let _straivLocation: StraivLocationPlugin | null = null;
let _bgGeo: BGPlugin | null = null;

const getStraivLocation = (): StraivLocationPlugin | null => {
  if (_straivLocation) return _straivLocation;
  try {
    _straivLocation = registerPlugin("StraivLocation") as StraivLocationPlugin;
    return _straivLocation;
  } catch {
    return null;
  }
};

const getBackgroundGeolocation = (): BGPlugin | null => {
  if (_bgGeo) return _bgGeo;
  for (const name of ["BackgroundGeolocation", "CapacitorCommunityBackgroundGeolocation"]) {
    try {
      const p = registerPlugin(name) as { addWatcher?: unknown } | null;
      if (p && typeof p.addWatcher === "function") {
        _bgGeo = p as BGPlugin;
        return _bgGeo;
      }
    } catch {}
  }
  return null;
};

/** Open app Settings. Call from user gesture (e.g. button click). */
export async function openLocationSettings(): Promise<void> {
  const StraivLocation = getStraivLocation();
  const BG = getBackgroundGeolocation();
  try {
    if (StraivLocation?.openSettings) {
      await StraivLocation.openSettings();
      return;
    }
    if (BG?.openSettings) {
      await BG.openSettings();
      return;
    }
  } catch {}
  if (Capacitor.getPlatform() === "ios") {
    window.open("app-settings:", "_self");
  }
}

/**
 * Ensures location is ready for outdoor run. On iOS, requires "Always" for background tracking.
 */
export async function ensureLocationForOutdoorRun(): Promise<EnsureLocationResult> {
  if (Capacitor.getPlatform() !== "ios") {
    try {
      const p = await Geolocation.checkPermissions();
      return p.location === "granted" ? { ok: true } : { ok: false, reason: "notGranted" };
    } catch {
      return { ok: false, reason: "notGranted" };
    }
  }

  const checkStatus = async (): Promise<LocationStatus> => {
    try {
      const StraivLocation = getStraivLocation();
      if (StraivLocation?.getLocationPermissionStatus) {
        const result = await StraivLocation.getLocationPermissionStatus();
        const raw = result?.status ?? (result as { value?: { status?: string } })?.value?.status ?? null;
        return (raw ?? "notDetermined") as LocationStatus;
      }
    } catch {}
    const p = await Geolocation.checkPermissions();
    if (p.location !== "granted") return "denied";
    return "whenInUse";
  };

  try {
    let status = await checkStatus();

    // Already on Always - perfect, nothing to do.
    if (status === "always") return { ok: true };

    if (status === "denied") {
      return { ok: false, reason: "denied" };
    }

    // For both notDetermined and whenInUse, try to upgrade to Always via native
    // plugin. iOS shows our proactive prompt with our usage description rather
    // than the system's automatic background-reminder dialog later on.
    //
    // CRITICAL: iOS suppresses requestAlwaysAuthorization after the user has
    // dismissed the Always prompt once, and the delegate never fires - which
    // would hang the Start button forever. The native plugin has its own 1.5s
    // safety timeout, but we add a JS-side belt-and-suspenders timeout as well.
    // PERFORMANCE: If we already have whenInUse, the user explicitly granted
    // foreground GPS - that's enough to start the run. Skip the Always-upgrade
    // request entirely (iOS only shows the prompt once anyway) so the Start
    // button responds instantly. Background tracking will simply be unavailable
    // until the user grants Always from Settings, which is handled later by the
    // run page if the location stops updating in background.
    if (status === "whenInUse") return { ok: true };

    const StraivLocation = getStraivLocation();
    if (StraivLocation?.requestAlwaysPermission) {
      try {
        const requestPromise = StraivLocation.requestAlwaysPermission();
        const timeoutPromise = new Promise<{ status?: string } | null>((resolve) =>
          setTimeout(() => resolve(null), 1200)
        );
        const result = await Promise.race([requestPromise, timeoutPromise]);
        const newStatus = (result?.status ?? null) as LocationStatus | null;
        if (newStatus === "always" || newStatus === "whenInUse") return { ok: true };
        if (newStatus === "denied") return { ok: false, reason: "denied" };
        // Timed out or got an unexpected response: fall back to current status check.
        const after = await checkStatus();
        if (after === "always" || after === "whenInUse") return { ok: true };
      } catch {
        // Fall through to Capacitor Geolocation request as a fallback.
      }
    }

    if (status === "notDetermined") {
      const req = await Geolocation.requestPermissions();
      if (req.location === "granted") return { ok: true };
      const post = await checkStatus();
      if (post === "always" || post === "whenInUse") return { ok: true };
      return { ok: false, reason: "denied" };
    }

    // status === "whenInUse" but the always-upgrade plugin call failed - accept whenInUse.
    if (status === "whenInUse") return { ok: true };

    const p = await Geolocation.checkPermissions();
    return p.location === "granted" ? { ok: true } : { ok: false, reason: "notGranted" };
  } catch (err) {
    console.warn("[runLocationPermission] Error:", err);
    try {
      const perm = await Geolocation.checkPermissions();
      return perm.location === "granted" ? { ok: true } : { ok: false, reason: "notGranted" };
    } catch {
      return { ok: false, reason: "notGranted" };
    }
  }
}
